/**
 * `canary scenarios` — drive the scenario candidate review queue.
 *
 * Minimal lifecycle CLI for Phase D of the scenario suite:
 *   list / get / claim / commit / reject / bundle
 *
 * Designed to be invoked by an agent (every prompt has a flag
 * equivalent — no interactive input required).
 *
 * @module
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { createHash } from 'node:crypto';
import { spawnSync } from 'node:child_process';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

interface ScenarioCandidate {
  id: string;
  fingerprint: string;
  status: 'pending' | 'claimed' | 'committed' | 'rejected' | 'duplicate';
  sourceTrigger: 'cache_failure' | 'l3_invocation' | 'sampled' | 'manual';
  sourceRunId: string | null;
  sourceStepIdx: number | null;
  hostSlug: string;
  featureSlug: string | null;
  s3KeyBundle: string;
  bundleBytes: number | null;
  bundleSha256: string | null;
  relatedToFingerprint: string | null;
  capturedAt: string;
  claimedBy: string | null;
  claimedAt: string | null;
  resolutionCommit: string | null;
  resolutionScenarioId: string | null;
  rejectionReason: string | null;
}

interface ListResponse {
  ok: boolean;
  error?: string;
  candidates?: ScenarioCandidate[];
}

interface SingleResponse {
  ok: boolean;
  error?: string;
  candidate?: ScenarioCandidate;
}

interface BundleResponse {
  ok: boolean;
  error?: string;
  url?: string;
  expiresInSeconds?: number;
  sha256?: string | null;
  bytes?: number | null;
}

function printHelp(): void {
  console.log(`canary scenarios — scenario candidate review queue

Subcommands:
  list                       List pending candidates (LIFO, newest first)
  get <id>                   Print a single candidate as JSON
  bundle <id>                Print the signed bundle URL (for piping into curl)
  claim <id>                 Claim a candidate; downloads + unzips the bundle
                             into apps/api/tests/scenarios/_pending/<id>/
  commit <id>                Mark candidate committed
                             --commit-sha <sha>      (required) git sha of the landing commit
                             --scenario-id <name>    (required) <host>__<feature>__<hash> in the suite
  reject <id> --reason <r>   Mark candidate rejected without committing

Common flags:
  --json                     Machine-readable output (for agents)
  --limit <n>                list: max rows to return (default 50, max 200)
  --claimed-by <name>        claim: who is claiming (default: $USER or 'agent')
  --workdir <path>           claim: where to unzip the bundle
                             (default: apps/api/tests/scenarios/_pending/<id>)
`);
}

export async function runScenarios(argv: string[]): Promise<void> {
  const sub = argv[0];
  if (!sub || sub === '--help' || sub === '-h') {
    printHelp();
    return;
  }

  const rest = argv.slice(1);
  const config = await resolveConfig(rest);
  const { apiUrl, token } = config;

  switch (sub) {
    case 'list':
      return handleList(rest, apiUrl, token);
    case 'get':
      return handleGet(rest, apiUrl, token);
    case 'bundle':
      return handleBundle(rest, apiUrl, token);
    case 'claim':
      return handleClaim(rest, apiUrl, token);
    case 'commit':
      return handleCommit(rest, apiUrl, token);
    case 'reject':
      return handleReject(rest, apiUrl, token);
    default:
      console.error(`Unknown subcommand: ${sub}`);
      printHelp();
      process.exit(1);
  }
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const limitStr = getArgValue(argv, '--limit');
  const limit = limitStr ? Number.parseInt(limitStr, 10) : 50;

  const path = `/superadmin/scenarios/candidates?limit=${limit}`;
  const result = await apiRequest<ListResponse>(apiUrl, token, 'GET', path);
  if (!result.ok || !result.candidates) {
    console.error(`Error: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result.candidates, null, 2));
    return;
  }

  if (result.candidates.length === 0) {
    console.log('No pending scenario candidates.');
    return;
  }
  console.log(`${result.candidates.length} pending candidate(s):`);
  for (const c of result.candidates) {
    const ageMin = Math.round((Date.now() - new Date(c.capturedAt).getTime()) / 60000);
    console.log(
      `  ${c.id}  trigger=${c.sourceTrigger}  host=${c.hostSlug}  age=${ageMin}m`
    );
  }
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Usage: canary scenarios get <id>');
    process.exit(1);
  }
  const result = await apiRequest<SingleResponse>(
    apiUrl,
    token,
    'GET',
    `/superadmin/scenarios/candidates/${id}`
  );
  if (!result.ok || !result.candidate) {
    console.error(`Error: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }
  console.log(JSON.stringify(result.candidate, null, 2));
}

async function handleBundle(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Usage: canary scenarios bundle <id>');
    process.exit(1);
  }
  const result = await apiRequest<BundleResponse>(
    apiUrl,
    token,
    'GET',
    `/superadmin/scenarios/candidates/${id}/bundle`
  );
  if (!result.ok || !result.url) {
    console.error(`Error: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  console.log(result.url);
}

async function handleClaim(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Usage: canary scenarios claim <id> [--claimed-by <name>] [--workdir <path>]');
    process.exit(1);
  }
  const claimedBy = getArgValue(argv, '--claimed-by') ?? process.env.USER ?? 'agent';
  const workdir =
    getArgValue(argv, '--workdir') ??
    path.resolve(process.cwd(), 'apps/api/tests/scenarios/_pending', id);

  const claim = await apiRequest<SingleResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/scenarios/candidates/${id}/claim`,
    { claimedBy }
  );
  if (!claim.ok || !claim.candidate) {
    console.error(`Claim failed: ${claim.error ?? 'unknown'}`);
    process.exit(1);
  }

  const bundleResp = await apiRequest<BundleResponse>(
    apiUrl,
    token,
    'GET',
    `/superadmin/scenarios/candidates/${id}/bundle`
  );
  if (!bundleResp.ok || !bundleResp.url) {
    console.error(`Bundle URL fetch failed: ${bundleResp.error ?? 'unknown'}`);
    process.exit(1);
  }

  fs.mkdirSync(workdir, { recursive: true });
  const dl = await fetch(bundleResp.url);
  if (!dl.ok) {
    console.error(`Bundle download failed: HTTP ${dl.status}`);
    process.exit(1);
  }
  const buf = Buffer.from(await dl.arrayBuffer());
  if (bundleResp.sha256) {
    const got = createHash('sha256').update(buf).digest('hex');
    if (got !== bundleResp.sha256) {
      console.error(`Bundle sha256 mismatch: expected ${bundleResp.sha256}, got ${got}`);
      process.exit(1);
    }
  }

  const zipPath = path.join(workdir, 'bundle.zip');
  fs.writeFileSync(zipPath, buf);

  // Try to unzip via the system `unzip` command; fall through with a hint
  // if unavailable so the operator can extract by hand.
  const unzip = spawnSync('unzip', ['-o', zipPath, '-d', workdir], {
    stdio: 'pipe',
    encoding: 'utf8',
  });
  const extracted = unzip.status === 0;
  if (extracted) {
    fs.unlinkSync(zipPath);
  }

  if (hasFlag(argv, '--json')) {
    console.log(
      JSON.stringify(
        {
          ok: true,
          candidate: claim.candidate,
          workdir,
          bundleZipPath: extracted ? null : zipPath,
        },
        null,
        2
      )
    );
    return;
  }
  if (extracted) {
    console.log(`Claimed ${id}; bundle unzipped at ${workdir}`);
    console.log(`  source: ${claim.candidate.sourceTrigger} on host ${claim.candidate.hostSlug}`);
    console.log(`  contents:`);
    for (const entry of fs.readdirSync(workdir).sort()) {
      console.log(`    ${entry}`);
    }
  } else {
    console.log(`Claimed ${id}; bundle saved at ${zipPath}`);
    console.log(`  (system 'unzip' not found; extract manually:`);
    console.log(`     unzip ${zipPath} -d ${workdir})`);
  }
}

async function handleCommit(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error(
      'Usage: canary scenarios commit <id> --commit-sha <sha> --scenario-id <id>'
    );
    process.exit(1);
  }
  const commitSha = getArgValue(argv, '--commit-sha');
  const scenarioId = getArgValue(argv, '--scenario-id');
  if (!commitSha || !scenarioId) {
    console.error('Error: --commit-sha and --scenario-id are required.');
    process.exit(1);
  }
  const result = await apiRequest<SingleResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/scenarios/candidates/${id}/commit`,
    { commitSha, scenarioId }
  );
  if (!result.ok || !result.candidate) {
    console.error(`Commit failed: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.candidate, null, 2));
    return;
  }
  console.log(`Marked ${id} committed → ${scenarioId} @ ${commitSha}`);
}

async function handleReject(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv[0];
  if (!id || id.startsWith('--')) {
    console.error('Usage: canary scenarios reject <id> --reason <text>');
    process.exit(1);
  }
  const reason = getArgValue(argv, '--reason');
  if (!reason) {
    console.error('Error: --reason is required.');
    process.exit(1);
  }
  const result = await apiRequest<SingleResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/scenarios/candidates/${id}/reject`,
    { reason }
  );
  if (!result.ok || !result.candidate) {
    console.error(`Reject failed: ${result.error ?? 'unknown'}`);
    process.exit(1);
  }
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(result.candidate, null, 2));
    return;
  }
  console.log(`Rejected ${id}: ${reason}`);
}
